import { InjectionToken } from '@angular/core';

/**
 * Supported portal brands. The active brand is derived from the application host
 * (see {@link getBrand}) so a single build can be served under multiple brands.
 */
export enum Brand {
  Salt = 'salt',
  RocketKor = 'rocketkor',
}

export interface BrandConfig {
  brand: Brand;
  /** Human-readable brand name (page title, brand labels). */
  displayName: string;
  /** Legal company name used in the footer copyright. */
  companyName: string;
  /** Wide wordmark logo (auth page). */
  logo: string;
  /** Logo shown at the top of the (dark) sidebar. Defaults to the wordmark where it reads on the sidebar bg. */
  sidebarLogo: string;
  /** Compact square icon (error/maintenance/search screens). */
  logoIcon: string;
  logoAlt: string;
  /** Path to the favicon asset. */
  favicon: string;
  /**
   * Externally hosted legal pages. When set, the auth footer links out to them
   * instead of opening the in-app legal documents.
   */
  legalUrls: { terms: string; privacyPolicy: string } | null;
  /** Display label for the mock-bank aggregation developer tool (SALT shows "TestBank"). */
  aggregationLabel: string;
}

export const BRAND_CONFIGS: Record<Brand, BrandConfig> = {
  [Brand.RocketKor]: {
    brand: Brand.RocketKor,
    displayName: 'RocketKOR',
    companyName: 'Rocket Financial, Inc.',
    logo: './assets/images/rocket-kor-logo-white.svg',
    // RocketKOR's sidebar is light, so it keeps the compact square mark (the white wordmark would vanish).
    sidebarLogo: './assets/images/rocket.svg',
    logoIcon: './assets/images/rocket.svg',
    logoAlt: 'RocketKOR Portal',
    favicon: './assets/images/rocket.svg',
    // RocketKOR's Terms and Privacy Policy are rendered in-app (with a PDF download).
    legalUrls: null,
    aggregationLabel: 'MocketBNK Aggregation',
  },
  [Brand.Salt]: {
    brand: Brand.Salt,
    displayName: 'Salt Treasury',
    companyName: 'Salt Treasury, Inc.',
    // Official white wordmark from salttreasury.com (shown on the dark auth panel).
    logo: './assets/images/salt-logo-white.png',
    // SALT's sidebar is dark navy, so it shows the full white wordmark (matches the auth screen).
    sidebarLogo: './assets/images/salt-logo-white.png',
    // Dark wordmark used on the (light) error/maintenance/search screens.
    logoIcon: './assets/images/salt-logo-dark.png',
    logoAlt: 'Salt Portal',
    favicon: './assets/images/salt-favicon.ico',
    // SALT hosts its legal copy on the marketing site (KOR-14864), so the footer links out.
    legalUrls: {
      terms: 'https://salttreasury.com/terms',
      privacyPolicy: 'https://salttreasury.com/privacy',
    },
    aggregationLabel: 'TestBank Aggregation',
  },
};

// TEMP: force a brand during local development so SALT branding can be previewed
// without a "salt" host (which the QA backend rejects via CORS). Set back to `null`
// to restore pure host-based detection locally. Deployed hosts are unaffected.
const LOCAL_DEV_BRAND: Brand | null = Brand.Salt;

const isLocalHost = (host: string): boolean => host.startsWith('localhost') || host.startsWith('127.0.0.1');

/**
 * Resolves the active brand from the host. A host containing the word "salt"
 * activates SALT branding; everything else (including "rocketkor" hosts and the
 * local default) falls back to RocketKOR.
 */
export const getBrand = (host: string = window.location.host): Brand => {
  if (LOCAL_DEV_BRAND && isLocalHost(host)) {
    return LOCAL_DEV_BRAND;
  }

  return host.toLowerCase().includes('salt') ? Brand.Salt : Brand.RocketKor;
};

export const getBrandConfig = (host?: string): BrandConfig => BRAND_CONFIGS[getBrand(host)];

// Root-provided with a default factory so the token is always resolvable — in the
// app and in any TestBed — without each injector needing to register it explicitly.
export const BRAND_CONFIG = new InjectionToken<BrandConfig>('Brand Config', {
  providedIn: 'root',
  factory: () => getBrandConfig(),
});
